import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Copy, RefreshCw, ShieldCheck, XCircle, Send, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  generated: "Draft",
  awaiting_approval: "Awaiting approval",
  approved: "Approved",
  ready_to_publish: "Ready to publish",
  rejected: "Rejected",
};
const STATUS_COLORS = {
  generated: "bg-zinc-100 text-zinc-700",
  awaiting_approval: "bg-yellow-100 text-yellow-800",
  approved: "bg-emerald-100 text-emerald-800",
  ready_to_publish: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

/**
 * Phase 2 — AI Social Post workflow (Ready-to-Publish semantics).
 *
 * Not labelled "Published" until a live Facebook/Instagram/GBP integration
 * is added. `ready_to_publish` is the terminal state for the save-only
 * workflow — a business copies the text and posts manually.
 */
export default function SocialPostSection({ job, business }) {
  const [post, setPost] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${job.id}/marketing/social-post`);
      setPost(data || null);
      if (data) setText(data.edited_text || data.generated_text || "");
    } catch { /* silent */ }
  };
  useEffect(() => { if (job?.id) load(); }, [job?.id]); // eslint-disable-line

  const enabled = business?.social_post_enabled !== false;
  if (!enabled || job?.status !== "completed") return null;

  const status = post?.status;
  const editable = !status || status === "generated" || status === "awaiting_approval" || status === "rejected";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/marketing/social-post/generate`, { platforms: ["facebook"] });
      setPost(data);
      setText(data.edited_text || data.generated_text);
      toast.success(post ? "Regenerated" : "AI post generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!post) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/social-posts/${post.id}`, { edited_text: text });
      setPost(data);
      toast.success("Edit saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      // Ensure latest edited text is persisted before submission.
      await api.patch(`/social-posts/${post.id}`, { edited_text: text });
      const { data } = await api.post(`/social-posts/${post.id}/submit`);
      setPost(data);
      toast.success("Submitted for approval");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social-posts/${post.id}/approve`);
      setPost(data);
      toast.success("Approved — ready to publish");
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social-posts/${post.id}/reject`);
      setPost(data);
      toast.success("Post rejected — regenerate to try again");
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text || post?.generated_text || "");
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="social-post-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-[#FF5A00]"/>
        <h3 className="font-heading text-lg font-bold">AI social media post</h3>
        {status && (
          <span
            data-testid="social-post-status-badge"
            className={`ml-auto text-[10px] uppercase tracking-widest font-mono-accent px-2 py-1 rounded ${STATUS_COLORS[status]}`}
          >
            {STATUS_LABEL[status]}
          </span>
        )}
      </div>

      {!post && (
        <div className="text-sm text-zinc-500 mb-3">
          Generate a warm, human-sounding social post from this completed job — powered by Gemini 3 Flash.
        </div>
      )}

      {post && (
        <>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            disabled={!editable}
            className="font-mono-accent text-sm mb-3"
            data-testid="social-post-textarea"
          />
          <div className="text-xs text-zinc-500 mb-3">
            Platforms intended: {post.platforms.join(", ")} · Regenerated {post.regenerate_count} time{post.regenerate_count === 1 ? "" : "s"}
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={generate}
          disabled={busy}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="social-post-generate-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1"/>
          {post ? "Regenerate" : "Generate with AI"}
        </Button>
        {post && editable && (
          <Button variant="outline" onClick={saveEdit} disabled={busy} data-testid="social-post-save-btn">
            Save edit
          </Button>
        )}
        {post && status === "generated" && (
          <Button variant="outline" onClick={submit} disabled={busy} data-testid="social-post-submit-btn">
            <Send className="w-4 h-4 mr-1"/> Submit for approval
          </Button>
        )}
        {post && (status === "generated" || status === "awaiting_approval") && (
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={approve}
            disabled={busy}
            data-testid="social-post-approve-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-1"/> Approve
          </Button>
        )}
        {post && status !== "rejected" && (
          <Button
            variant="outline"
            onClick={reject}
            disabled={busy || status === "ready_to_publish"}
            data-testid="social-post-reject-btn"
          >
            <XCircle className="w-4 h-4 mr-1"/> Reject
          </Button>
        )}
        {post && status === "ready_to_publish" && (
          <>
            <Button variant="outline" onClick={copyText} disabled={busy} data-testid="social-post-copy-btn">
              <Copy className="w-4 h-4 mr-1"/> Copy post text
            </Button>
            <div className="ml-2 text-xs text-emerald-700 flex items-center gap-1">
              <Check className="w-3 h-3"/> Ready — paste into Facebook and publish manually.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
